import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import html2canvas from 'html2canvas';
import { PoliticianData, BenchmarkData } from '../types';
import ShareableCard from './ShareableCard';
import './ShareCardModal.css';

interface ShareCardModalProps {
  politician: PoliticianData;
  benchmarks: Record<string, BenchmarkData>;
  timeframe: string;
  onClose: () => void;
}

// Generated card cache
const cardCache = new Map<string, string>();

const ShareCardModal: React.FC<ShareCardModalProps> = ({
  politician,
  benchmarks,
  timeframe,
  onClose,
}) => {
  const { t } = useTranslation();
  const cardRef = useRef<HTMLDivElement>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImage, setGeneratedImage] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [downloaded, setDownloaded] = useState(false);

  // Check cache on mount
  useEffect(() => {
    const cacheKey = `${politician.id}-${timeframe}-${politician.currentValue}`;
    const cached = cardCache.get(cacheKey);
    if (cached) {
      setGeneratedImage(cached);
    }
  }, [politician.id, timeframe, politician.currentValue]);

  const generateImage = useCallback(async (): Promise<string | null> => {
    if (generatedImage) return generatedImage;

    const cacheKey = `${politician.id}-${timeframe}-${politician.currentValue}`;
    const cached = cardCache.get(cacheKey);
    if (cached) {
      setGeneratedImage(cached);
      return cached;
    }

    if (!cardRef.current) return null;

    setIsGenerating(true);
    try {
      const canvas = await html2canvas(cardRef.current, {
        scale: 1,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#fafaf9',
        width: 1200,
        height: 630,
      });
      const dataUrl = canvas.toDataURL('image/png');
      cardCache.set(cacheKey, dataUrl);
      setGeneratedImage(dataUrl);
      return dataUrl;
    } catch (error) {
      console.error('Failed to generate image:', error);
      return null;
    } finally {
      setIsGenerating(false);
    }
  }, [politician.id, politician.currentValue, timeframe, generatedImage]);

  const handleDownload = async (): Promise<boolean> => {
    const imageUrl = await generateImage();
    if (!imageUrl) return false;

    const link = document.createElement('a');
    link.download = `${politician.name.replace(/\s+/g, '-').toLowerCase()}-capitol-alpha.png`;
    link.href = imageUrl;
    link.click();

    setDownloaded(true);
    return true;
  };

  const handleCopyImage = async () => {
    const imageUrl = await generateImage();
    if (!imageUrl) return;

    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      await navigator.clipboard.write([
        new ClipboardItem({
          [blob.type]: blob,
        }),
      ]);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy image:', error);
      try {
        await navigator.clipboard.writeText(window.location.href);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch {
        console.error('Clipboard not available');
      }
    }
  };

  const handleShareTwitter = () => {
    const returnPct = ((politician.currentValue - 10000) / 10000) * 100;
    const sign = returnPct >= 0 ? '+' : '';
    const shareText = `${politician.name} made ${sign}${returnPct.toFixed(1)}% in ${timeframe} trading stocks while serving in Congress.\n\nFind top performers and copy their trades:`;
    const shareUrl = 'https://capitol-alpha.com';

    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(twitterUrl, '_blank', 'width=600,height=400');
  };

  const handleShareFacebook = () => {
    const shareUrl = 'https://capitol-alpha.com';
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
    window.open(facebookUrl, '_blank', 'width=600,height=400');
  };

  const handleShareReddit = () => {
    const returnPct = ((politician.currentValue - 10000) / 10000) * 100;
    const sign = returnPct >= 0 ? '+' : '';
    const shareTitle = `${politician.name}'s stock trades returned ${sign}${returnPct.toFixed(1)}% in ${timeframe} - Copy the top performers`;
    const shareUrl = 'https://capitol-alpha.com';

    const redditUrl = `https://www.reddit.com/submit?url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(shareTitle)}`;
    window.open(redditUrl, '_blank', 'width=600,height=600');
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="share-card-modal-backdrop" onClick={handleBackdropClick}>
      <div className="share-card-modal">
        <button className="share-card-modal-close" onClick={onClose}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>

        <h3 className="share-card-modal-title">{t('shareCard:title')}</h3>
        <p className="share-card-modal-subtitle">{t('shareCard:subtitle')}</p>

        {/* Preview - show generated image or scaled card */}
        <div className="share-card-preview-container">
          {generatedImage ? (
            <img
              src={generatedImage}
              alt="Share card preview"
              className="share-card-preview-image"
            />
          ) : (
            <div className="share-card-preview-wrapper">
              <ShareableCard
                ref={cardRef}
                politician={politician}
                benchmarks={benchmarks}
                timeframe={timeframe}
              />
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="share-card-actions">
          <button
            className="share-card-action-btn primary"
            onClick={handleDownload}
            disabled={isGenerating}
          >
            {isGenerating ? (
              <span className="share-card-spinner" />
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
            )}
            {t('shareCard:download')}
          </button>

          <button
            className={`share-card-action-btn ${copied ? 'copied' : ''}`}
            onClick={handleCopyImage}
            disabled={isGenerating}
          >
            {copied ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
              </svg>
            )}
            {copied ? t('shareCard:copied') : t('shareCard:copyImage')}
          </button>
        </div>

        <div className="share-card-divider">
          <span>{t('shareCard:orShareOn')}</span>
        </div>

        <div className="share-card-social">
          <button className="share-card-social-btn twitter" onClick={handleShareTwitter} disabled={isGenerating}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
            </svg>
            <span>{t('shareCard:shareOnX')}</span>
          </button>

          <button className="share-card-social-btn reddit" onClick={handleShareReddit} disabled={isGenerating}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z" />
            </svg>
            <span>{t('shareCard:shareOnReddit')}</span>
          </button>

          <button className="share-card-social-btn facebook" onClick={handleShareFacebook} disabled={isGenerating}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
            </svg>
            <span>{t('shareCard:shareOnFacebook')}</span>
          </button>
        </div>

        <div className={`share-card-tip ${downloaded ? 'highlight' : ''}`}>
          {downloaded ? (
            <>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <span>{t('shareCard:imageDownloaded')}</span>
            </>
          ) : (
            <span>{t('shareCard:tip')}</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShareCardModal;
